import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  CalendarDays,
  CheckCircle2,
  Clock,
  HeartHandshake,
  Mail,
  MapPin,
  Newspaper,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import PageBanner from "../components/PageBanner";
import { localizedUrl } from "../lib/fetchLocalized";
import AppointmentRequestModal from "../components/schedule/AppointmentRequestModal";
import WaitlistModal from "../components/schedule/WaitlistModal";
import { formatTimeRange } from "../lib/eventTime";

const fallbackEvent = {
  id: "agm-upcoming-announcement",
  eventName: "Upcoming AGM Seva and Vedant Gathering",
  eventDate: "",
  eventLocation: "USA and India updates will be announced by AGM",
  description:
    "AGM will use this page to publish upcoming satsang, seva, program, donor and volunteer updates with full details for supporters in the USA, India and the wider diaspora.",
  eventImage: "/assets/agm/home-usa-times-square-yoga.jpg",
  focus: ["Seva updates", "Vedantic wisdom", "Volunteer opportunities", "Donation clarity"],
  isFallback: true,
};

// defaultAgenda is built inside the component via getDefaultAgenda(t) to enable i18n

// interestOptions and audienceTypes are built inside the component via t() to enable i18n

function normalizeBase(url) {
  return url?.replace(/\/$/, "");
}

function getApiBaseCandidates() {
  const explicitBase = normalizeBase(import.meta.env.VITE_APP_BASE_URL);
  const viteBase = normalizeBase(import.meta.env.VITE_API_BASE_URL);
  const appUrl = normalizeBase(import.meta.env.VITE_APP_API_URL);

  return [explicitBase, viteBase, appUrl ? `${appUrl}/api` : null, "/api/nextjs"].filter(Boolean);
}

function formatDate(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function eventTimestamp(dateValue) {
  const time = new Date(dateValue).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

async function fetchJsonFromCandidates(path, options = {}) {
  const bases = getApiBaseCandidates();
  let lastError;

  for (const base of bases) {
    try {
      const response = await fetch(`${base}${path}`, options);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || `Request failed at ${base}${path}`);
      return payload;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Request failed");
}

const FormField = ({ label, children, error }) => (
  <label className="grid gap-2 text-sm font-semibold text-[#14201f]">
    {label}
    {children}
    {error && <span className="text-xs font-medium text-[#9d1f1c]">{error}</span>}
  </label>
);

// Shared event card used by both the upcoming grid and the Past Events section
const EventCard = ({ event, isPast = false, onRegister, onJoinWaitlist }) => {
  const { t } = useTranslation();
  const booking = event.booking;
  const canRegister = !isPast && booking && !booking.isFull;
  const isFull = !isPast && booking && booking.isFull;

  return (
    <article className="flex flex-col overflow-hidden rounded-lg border border-[#ead9cd] bg-[#fffaf5] shadow-sm">
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={event.eventImage || fallbackEvent.eventImage}
          alt={event.eventName}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9d1f1c]">
          {formatDate(event.eventDate) || t("events.dateToBeAnnounced")}
        </p>
        {formatTimeRange(event.startTime, event.endTime) && (
          <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-[#52615f]">
            <Clock className="h-3.5 w-3.5 shrink-0 text-[#9d1f1c]" />
            {formatTimeRange(event.startTime, event.endTime)}
          </p>
        )}
        <h3 className="mt-3 text-xl font-semibold leading-tight text-[#14201f]">
          {event.eventName}
        </h3>
        <p className="mt-4 text-sm leading-7 text-[#52615f]">{event.description}</p>
        <p className="mt-5 flex gap-2 text-sm font-semibold text-[#4b2418]">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
          {event.eventLocation || t("events.locationToBeAnnounced")}
        </p>
        {(canRegister || isFull || event._id) && (
          <div className="mt-auto pt-6">
            {canRegister && (
              <button
                type="button"
                onClick={() => onRegister?.(event)}
                className="mb-4 w-full rounded-md bg-[#5a0000] px-4 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#8a0000]"
              >
                Register
              </button>
            )}
            {isFull && (
              <button
                type="button"
                onClick={() => onJoinWaitlist?.(event)}
                className="mb-4 w-full rounded-md border border-[#5a0000] bg-white px-4 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#5a0000] transition hover:bg-[#fdf3ee]"
              >
                Full — Join the waitlist
              </button>
            )}
            {event._id && (
              <Link
                to={`/events/${event._id}`}
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#9d1f1c] transition hover:text-[#7f1916]"
              >
                View details
                <ArrowRight size={16} />
              </Link>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const UpcomingEvent = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();

  const defaultAgenda = [
    [t("events.agenda.invocation.title"), t("events.agenda.invocation.text")],
    [t("events.agenda.vedantLife.title"), t("events.agenda.vedantLife.text")],
    [t("events.agenda.programUpdates.title"), t("events.agenda.programUpdates.text")],
    [t("events.agenda.supporterBriefing.title"), t("events.agenda.supporterBriefing.text")],
  ];

  const interestOptions = [
    t("events.interests.upcomingEvents"),
    t("events.interests.vedantSatsang"),
    t("events.interests.healthcareSeva"),
    t("events.interests.educationSkilling"),
    t("events.interests.volunteerOpportunities"),
    t("events.interests.donorUpdates"),
  ];

  const audienceTypes = [
    t("events.audienceTypes.devotee"),
    t("events.audienceTypes.donor"),
    t("events.audienceTypes.volunteer"),
    t("events.audienceTypes.student"),
    t("events.audienceTypes.communityOrganizer"),
    t("events.audienceTypes.mediaPartner"),
  ];
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    country: region.label || "",
    audienceType: audienceTypes[0],
    interests: [t("events.interests.upcomingEvents"), t("events.interests.vedantSatsang")],
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [waitlistTarget, setWaitlistTarget] = useState(null);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadEvents() {
      setIsLoadingEvents(true);
      try {
        const eventApi = import.meta.env.VITE_APP_EVENT_API;
        const payload = eventApi
          ? await fetch(localizedUrl(eventApi)).then(async (response) => {
              const data = await response.json().catch(() => ({}));
              if (!response.ok) throw new Error(data.message || "Unable to load events");
              return data;
            })
          : await fetchJsonFromCandidates("/events");

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const datedEvents = (payload.data || []).filter((event) => event?.eventDate);

        const nextEvents = datedEvents
          .filter((event) => eventTimestamp(event.eventDate) >= now.getTime())
          .sort((a, b) => eventTimestamp(a.eventDate) - eventTimestamp(b.eventDate))
          .slice(0, 6);

        const previousEvents = datedEvents
          .filter((event) => eventTimestamp(event.eventDate) < now.getTime())
          .sort((a, b) => eventTimestamp(b.eventDate) - eventTimestamp(a.eventDate))
          .slice(0, 6);

        if (!cancelled) {
          setEvents(nextEvents);
          setPastEvents(previousEvents);
        }
      } catch (error) {
        if (!cancelled) {
          setEvents([]);
          setPastEvents([]);
        }
      } finally {
        if (!cancelled) setIsLoadingEvents(false);
      }
    }

    loadEvents();

    return () => {
      cancelled = true;
    };
  }, []);

  const featuredEvent = events[0] || fallbackEvent;
  const supportingEvents = events.slice(1);
  const fallbackFocus = [
    t("events.fallbackFocus.sevaUpdates"),
    t("events.fallbackFocus.vedanticWisdom"),
    t("events.fallbackFocus.volunteerOpportunities"),
    t("events.fallbackFocus.donationClarity"),
  ];
  const eventFocus = featuredEvent.isFallback
    ? fallbackFocus
    : featuredEvent.focus || fallbackFocus;
  const eventImage = featuredEvent.eventImage || fallbackEvent.eventImage;

  const eventDetails = useMemo(
    () => [
      [
        CalendarDays,
        t("events.labelDate"),
        formatDate(featuredEvent.eventDate) || t("events.dateToBeAnnounced"),
      ],
      [
        MapPin,
        t("events.labelLocation"),
        featuredEvent.eventLocation || t("events.locationToBeAnnounced"),
      ],
      // Time only appears once an organiser sets it in the CMS.
      ...(formatTimeRange(featuredEvent.startTime, featuredEvent.endTime)
        ? [
            [
              Clock,
              t("events.labelTime"),
              formatTimeRange(featuredEvent.startTime, featuredEvent.endTime),
            ],
          ]
        : []),
      [
        Sparkles,
        t("events.labelFormat"),
        // The CMS "Format" field wins; blank keeps the previous generic wording.
        featuredEvent.eventFormat?.trim()
          ? featuredEvent.eventFormat.trim()
          : featuredEvent.isFallback
            ? t("events.announcementPending")
            : t("events.publicEventUpdate"),
      ],
    ],
    [featuredEvent, t]
  );

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const toggleInterest = (interest) => {
    setForm((current) => {
      const exists = current.interests.includes(interest);
      const interests = exists
        ? current.interests.filter((item) => item !== interest)
        : [...current.interests, interest];
      return { ...current, interests };
    });
    setErrors((current) => ({ ...current, interests: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.fullName.trim()) nextErrors.fullName = t("events.validation.nameRequired");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      nextErrors.email = t("events.validation.emailRequired");
    if (!form.interests.length) nextErrors.interests = t("events.validation.interestsRequired");
    if (!form.consent) nextErrors.consent = t("events.validation.consentRequired");
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitToConnectFallback = async (payload) => {
    const formData = new FormData();
    formData.append("fullName", payload.fullName);
    formData.append("email", payload.email);
    formData.append("subject", "Newsletter signup from Upcoming Event page");
    formData.append(
      "message",
      [
        `Phone: ${payload.phone || "Not provided"}`,
        `Country: ${payload.country || "Not provided"}`,
        `Audience: ${payload.audienceType || "Not provided"}`,
        `Region: ${payload.preferredRegion || "Not provided"}`,
        `Interests: ${payload.interests.join(", ")}`,
        `Message: ${payload.message || "Not provided"}`,
      ].join("\n")
    );

    const bases = getApiBaseCandidates();
    let lastError;
    for (const base of bases) {
      try {
        const response = await fetch(`${base}/connect`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || "Connect fallback failed");
        }
        return true;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("Unable to save signup");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = {
      ...form,
      fullName: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      country: form.country.trim(),
      message: form.message.trim(),
      preferredRegion: region.key || region.label,
      source: "upcoming-event-page",
    };

    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await fetchJsonFromCandidates("/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setStatus({
        type: "success",
        message: t("events.status.successNewsletter"),
      });
      setForm((current) => ({
        ...current,
        fullName: "",
        email: "",
        phone: "",
        message: "",
        consent: false,
      }));
    } catch (error) {
      try {
        await submitToConnectFallback(payload);
        setStatus({
          type: "success",
          message: t("events.status.successFallback"),
        });
        setForm((current) => ({
          ...current,
          fullName: "",
          email: "",
          phone: "",
          message: "",
          consent: false,
        }));
      } catch (fallbackError) {
        setStatus({
          type: "error",
          message: t("events.status.error"),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Event registration — reuses the shared appointment/registration modal from the Schedule page
  const openEventRegistration = (event) => {
    if (!event?.booking) return;
    const formattedDate = formatDate(event.eventDate) || "Not Available";
    setSelectedScheduleItem({
      id: event.booking.scheduleId,
      type: "event",
      availableSeats: event.booking.availableSeats,
      location: event.eventLocation,
      startDate: formattedDate,
      endDate: formattedDate,
      registrationFields: event.registrationFields || [],
      registrationNote: event.registrationNote || "",
      showSeatsRemaining: event.showSeatsRemaining !== false,
      // Carries the venue time onto the registration record, so confirmation
      // emails say when to arrive and not just which day.
      time: formatTimeRange(event.startTime, event.endTime) || "",
      request: true,
    });
    setRegistrationModalOpen(true);
  };

  const handleCloseRegistration = () => {
    setRegistrationModalOpen(false);
    setSelectedScheduleItem(null);
  };

  // Full event → offer the notify-style waitlist instead of a dead chip.
  const openWaitlist = (event) => {
    if (!event?.booking?.scheduleId) return;
    setWaitlistTarget({
      scheduleId: event.booking.scheduleId,
      eventName: event.eventName,
    });
  };

  const handleAppointmentSubmit = (appointmentData) => {
    console.log("Appointment submitted:", appointmentData);
    // You can add additional handling here, such as updating UI state
  };

  return (
    <main className="overflow-x-hidden bg-[#fffaf5] text-[#14201f]">
      <PageBanner
        {...(siteConfig.upcomingEventsPage?.banner || {})}
        rightSlot={
          <article className="rounded-lg border border-white/18 bg-white/12 p-6 shadow-2xl backdrop-blur-md">
            {/* Logo lockup — the card is shared and screenshotted on its own,
                so it has to carry the mission's identity. */}
            <div className="mb-5 flex items-center gap-3">
              <img
                src="/assets/Avdheshanandg mission logo.png"
                alt="AvdheshanandG Mission"
                className="h-9 w-auto"
              />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#f5c45e]">
                AvdheshanandG Mission
              </span>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#f5c45e]">
              {t("events.featuredUpdate")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight">{featuredEvent.eventName}</h2>
            <p className="mt-4 text-sm leading-7 text-white/78">{featuredEvent.description}</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {eventDetails.map(([Icon, label, value]) => (
                <div
                  key={label}
                  className="flex gap-3 rounded-md border border-white/12 bg-white/8 p-4"
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#f5c45e]" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/55">
                      {label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-white">{value}</p>
                  </div>
                </div>
              ))}
            </div>
            {(featuredEvent.booking || featuredEvent._id) && (
              <div className="mt-6 grid gap-3">
                {featuredEvent.booking &&
                  (featuredEvent.booking.isFull ? (
                    <button
                      type="button"
                      onClick={() => openWaitlist(featuredEvent)}
                      className="inline-flex w-full items-center justify-center rounded-md border border-white/60 bg-white/10 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-white/20"
                    >
                      Full — Join the waitlist
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openEventRegistration(featuredEvent)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#5a0000] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#8a0000]"
                    >
                      Register
                    </button>
                  ))}
                {featuredEvent._id && (
                  <Link
                    to={`/events/${featuredEvent._id}`}
                    className="inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#f5c45e] transition hover:text-white"
                  >
                    View details
                    <ArrowRight size={16} />
                  </Link>
                )}
              </div>
            )}
          </article>
        }
      />

      <section className="py-20 md:py-28">
        <div className="container mx-auto grid gap-10 px-4 sm:px-6 lg:grid-cols-[0.92fr_1.08fr] lg:px-8">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
              {t("events.eyebrowEventDetail")}
            </p>
            <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
              {t("events.detailHeading")}
            </h2>
            <p className="mt-6 text-base leading-8 text-[#52615f] md:text-lg">
              {t("events.detailDescription")}
            </p>
            <div className="mt-8 grid gap-3">
              {eventFocus.map((item) => (
                <div key={item} className="flex gap-3 text-sm leading-7 text-[#4f3d34]">
                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {[
              [
                Sparkles,
                t("events.cards.spiritualPurpose.title"),
                t("events.cards.spiritualPurpose.text"),
              ],
              [
                HeartHandshake,
                t("events.cards.sevaPathways.title"),
                t("events.cards.sevaPathways.text"),
              ],
              [
                ShieldCheck,
                t("events.cards.donorClarity.title"),
                t("events.cards.donorClarity.text"),
              ],
              [
                Newspaper,
                t("events.cards.newsletterReady.title"),
                t("events.cards.newsletterReady.text"),
              ],
            ].map(([Icon, title, text]) => (
              <article
                key={title}
                className="rounded-lg border border-[#ead9cd] bg-white p-6 shadow-sm"
              >
                <Icon className="h-7 w-7 text-[#9d1f1c]" />
                <h3 className="mt-5 text-xl font-semibold text-[#14201f]">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-[#52615f]">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.85fr]">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
                {t("events.eyebrowProgramFlow")}
              </p>
              <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
                {t("events.programFlowHeading")}
              </h2>
              <div className="mt-10 grid gap-4">
                {defaultAgenda.map(([title, text], index) => (
                  <article
                    key={title}
                    className="grid gap-4 rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-5 sm:grid-cols-[72px_1fr]"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-md bg-[#4b2418] text-lg font-semibold text-white">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#14201f]">{title}</h3>
                      <p className="mt-2 text-sm leading-7 text-[#52615f]">{text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-7">
              <Bell className="h-8 w-8 text-[#9d1f1c]" />
              <h3 className="mt-5 text-2xl font-semibold text-[#14201f]">
                {t("events.whoShouldJoin")}
              </h3>
              <div className="mt-6 grid gap-4">
                {[
                  t("events.whoItems.knownSwamiji"),
                  t("events.whoItems.newSupporters"),
                  t("events.whoItems.usaDonors"),
                  t("events.whoItems.volunteers"),
                ].map((item) => (
                  <div key={item} className="flex gap-3 text-sm leading-7 text-[#4f3d34]">
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section id="newsletter" className="py-20 md:py-28">
        <div className="container mx-auto grid gap-10 px-4 sm:px-6 lg:grid-cols-[0.88fr_1.12fr] lg:px-8">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
              {t("events.eyebrowEmailerList")}
            </p>
            <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
              {t("events.emailerHeading")}
            </h2>
            <p className="mt-6 text-base leading-8 text-[#52615f] md:text-lg">
              {t("events.emailerDescription")}
            </p>
            <div className="mt-8 rounded-lg border border-[#ead9cd] bg-white p-6 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#9d1f1c]">
                {t("events.whatYouWillReceive")}
              </p>
              <div className="mt-5 grid gap-3">
                {interestOptions.slice(0, 5).map((item) => (
                  <div key={item} className="flex gap-3 text-sm leading-7 text-[#4f3d34]">
                    <Mail className="mt-1 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-[#ead9cd] bg-white p-6 shadow-sm md:p-8"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <FormField label={t("events.form.fullName")} error={errors.fullName}>
                <input
                  value={form.fullName}
                  onChange={(event) => updateForm("fullName", event.target.value)}
                  className="rounded-md border border-[#d8c7bb] px-4 py-3 outline-none transition focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15"
                  placeholder={t("events.form.placeholders.yourName")}
                />
              </FormField>
              <FormField label={t("events.form.emailAddress")} error={errors.email}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => updateForm("email", event.target.value)}
                  className="rounded-md border border-[#d8c7bb] px-4 py-3 outline-none transition focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15"
                  placeholder="you@example.com"
                />
              </FormField>
              <FormField label={t("events.form.phoneWhatsApp")}>
                <input
                  value={form.phone}
                  onChange={(event) => updateForm("phone", event.target.value)}
                  className="rounded-md border border-[#d8c7bb] px-4 py-3 outline-none transition focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15"
                  placeholder="+1..."
                />
              </FormField>
              <FormField label={t("events.form.country")}>
                <input
                  value={form.country}
                  onChange={(event) => updateForm("country", event.target.value)}
                  className="rounded-md border border-[#d8c7bb] px-4 py-3 outline-none transition focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15"
                  placeholder={t("events.form.placeholders.country")}
                />
              </FormField>
              <FormField label={t("events.form.joiningAs")}>
                <select
                  value={form.audienceType}
                  onChange={(event) => updateForm("audienceType", event.target.value)}
                  className="rounded-md border border-[#d8c7bb] bg-white px-4 py-3 outline-none transition focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15"
                >
                  {audienceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("events.form.messageNote")}>
                <input
                  value={form.message}
                  onChange={(event) => updateForm("message", event.target.value)}
                  className="rounded-md border border-[#d8c7bb] px-4 py-3 outline-none transition focus:border-[#9d1f1c] focus:ring-2 focus:ring-[#9d1f1c]/15"
                  placeholder={t("events.form.placeholders.optional")}
                />
              </FormField>
            </div>

            <div className="mt-6">
              <p className="text-sm font-semibold text-[#14201f]">{t("events.updatesIWant")}</p>
              {errors.interests && (
                <p className="mt-2 text-xs font-medium text-[#9d1f1c]">{errors.interests}</p>
              )}
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {interestOptions.map((interest) => (
                  <label
                    key={interest}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-sm font-medium transition ${
                      form.interests.includes(interest)
                        ? "border-[#9d1f1c] bg-[#fff4ee] text-[#4b2418]"
                        : "border-[#ead9cd] bg-white text-[#52615f]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.interests.includes(interest)}
                      onChange={() => toggleInterest(interest)}
                      className="h-4 w-4 accent-[#9d1f1c]"
                    />
                    {interest}
                  </label>
                ))}
              </div>
            </div>

            <label className="mt-6 flex gap-3 text-sm leading-6 text-[#52615f]">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(event) => updateForm("consent", event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-[#9d1f1c]"
              />
              <span>
                {t("events.consentText")}
                {errors.consent && (
                  <span className="mt-1 block text-xs font-medium text-[#9d1f1c]">
                    {errors.consent}
                  </span>
                )}
              </span>
            </label>

            {status.message && (
              <div
                className={`mt-6 rounded-md border p-4 text-sm font-medium ${
                  status.type === "success"
                    ? "border-[#ead9cd] bg-[#fff4ee] text-[#4b2418]"
                    : "border-[#f1b8b6] bg-[#fff5f5] text-[#9d1f1c]"
                }`}
              >
                {status.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? t("events.form.saving") : t("events.form.addToEmailerList")}
              <Send size={18} />
            </button>
          </form>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
                {t("events.eyebrowEventCalendar")}
              </p>
              <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
                {t("events.calendarHeading")}
              </h2>
              <p className="mt-5 text-base leading-8 text-[#52615f] md:text-lg">
                {t("events.calendarDescription")}
              </p>
            </div>
            <Link
              to="/programs"
              className="inline-flex w-fit items-center gap-3 rounded-md bg-[#4b2418] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916]"
            >
              {t("events.explorePrograms")}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {isLoadingEvents ? (
              <div className="rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-6 text-sm text-[#52615f]">
                {t("events.loadingEvents")}
              </div>
            ) : (
              (events.length ? events : [fallbackEvent]).map((event) => (
                <EventCard
                  key={event._id || event.id}
                  event={event}
                  onRegister={openEventRegistration}
                  onJoinWaitlist={openWaitlist}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {!isLoadingEvents && pastEvents.length > 0 && (
        <section className="bg-white pb-20 md:pb-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
                Past Events
              </p>
              <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
                Recent gatherings and programs
              </h2>
              <p className="mt-5 text-base leading-8 text-[#52615f] md:text-lg">
                Revisit programs that have concluded — open any event to browse its photos, reels
                and full videos.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {pastEvents.map((event) => (
                <EventCard key={event._id || event.id} event={event} isPast />
              ))}
            </div>
          </div>
        </section>
      )}

      {registrationModalOpen && selectedScheduleItem && (
        <AppointmentRequestModal
          isOpen={registrationModalOpen}
          onClose={handleCloseRegistration}
          scheduleItem={selectedScheduleItem}
          onSubmit={handleAppointmentSubmit}
        />
      )}

      <WaitlistModal
        isOpen={Boolean(waitlistTarget)}
        onClose={() => setWaitlistTarget(null)}
        scheduleId={waitlistTarget?.scheduleId}
        eventName={waitlistTarget?.eventName}
      />
    </main>
  );
};

export default UpcomingEvent;
